import React, { memo, useMemo, useCallback, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import cn from 'classnames';
import { Icon, IconButton, Image } from 'components';
import Modal from 'components/Modal';
import { Button, Form, Input } from 'components/form';
import { REGEX } from 'lib/utils/regex';
import { FlagshipEventRequest } from 'lib/models/flagship-event';
import { InsightRequest } from 'lib/models/insight';
import { getRandomInt } from 'utils/format';
import styles from './TeamAndEventModal.module.scss';

export interface TeamAndEventModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (data: FlagshipEventRequest | InsightRequest) => void;
  title: string;
  isEdit: boolean;
  index: number | null;
  onRemove: (index: number) => void;
  defaultValues: FlagshipEventRequest | InsightRequest;
}

export const TeamAndEventModal: React.FC<TeamAndEventModalProps> = memo(
  ({
    isOpen,
    onConfirm,
    onCancel,
    title,
    isEdit = false,
    index,
    onRemove,
    defaultValues,
  }: TeamAndEventModalProps) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const values = { ...defaultValues };
    delete values?.icon;
    const {
      register,
      handleSubmit,
      watch,
      setValue,
      reset,
      formState: { errors, isSubmitting },
    } = useForm({ defaultValues });
    const currentImage =
      defaultValues?.icon ||
      defaultValues?.avatar_url ||
      defaultValues?.icon_url;
    const imageFiles = selectedFile || watch('icon');
    const createObjectURL = useCallback((files) => {
      if (files instanceof FileList && files.length > 0) {
        return URL.createObjectURL(files[0]);
      } else if (files instanceof File) {
        return URL.createObjectURL(files);
      }
      return files;
    }, []);

    const eventImage = createObjectURL(imageFiles);
    const number = useMemo(() => getRandomInt(1, 21), []);
    const defaultImage = `huzzle-gradient-${number}.jpg`;
    const firstInputLabel = 'title';
    const secondInputLabel = 'url';

    const onSubmit: SubmitHandler<FlagshipEventRequest | InsightRequest> = (
      data
    ) => {
      if (selectedFile) {
        data.icon = selectedFile;
      } else if (currentImage) {
        if (currentImage && currentImage instanceof File) {
          data.icon = currentImage;
        } else if (currentImage && typeof currentImage === 'string') {
          data.icon_url = currentImage;
        }
      }
      onConfirm({ ...data, ...{ defaultImage } });
      reset();
      setSelectedFile(null);
    };

    const [showFileSizeError, setShowFileSizeError] = useState<boolean>(false);

    const onChangeBanner = (e: React.ChangeEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      const files: FileList = target.files as FileList;
      if (files && files.length > 0) {
        if (files[0].size / 1000 < 5001) {
          setShowFileSizeError(false);
          setSelectedFile(files[0]);
          setValue('icon', files, { shouldDirty: true });
        } else {
          setShowFileSizeError(true);
        }
      }
    };

    const availableEventImage = eventImage
      ? eventImage
      : isEdit && defaultValues?.icon_url
      ? defaultValues?.icon_url
      : defaultImage;

    return (
      <>
        <Modal
          contentClassName={styles.TeamAndEventModal}
          isOpen={isOpen}
          onRequestClose={onCancel}
          isFullScreen={false}
        >
          <IconButton
            size="small"
            iconName="close"
            className={styles.cancelIcon}
            onClick={onCancel}
          />
          <Form
            autoComplete="off"
            className={styles.EventsNewEvent}
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit(onSubmit)(e);
            }}
            id="modal-form"
          >
            <div className={styles.content}>
              <div className={styles.title}>{title}</div>
              <div className={styles.TeamAndEventModalCard}>
                <div className={styles.TeamAndEventModalCardImage}>
                  <div className={styles.TeamAndEventModalImageWrapper}>
                    <Image
                      src={availableEventImage}
                      source={
                        eventImage
                          ? undefined
                          : isEdit && defaultValues?.icon_url
                          ? undefined
                          : 'aws'
                      }
                      alt="Upload image"
                      className={styles.EventsNewEventImage}
                    />
                    <div className={styles.TeamAndEventModalImageUpload}>
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          id={`event-upload-banner-image`}
                          hidden
                          onChange={onChangeBanner}
                        />
                        <Button
                          type="button"
                          color="white"
                          className={styles.bannerUploadButton}
                        >
                          <label htmlFor={`event-upload-banner-image`}>
                            Change Cover photo
                          </label>
                        </Button>
                      </div>
                    </div>
                  </div>
                  {showFileSizeError ? (
                    <div className={styles['error-container']}>
                      <Icon iconName={'info'}></Icon>
                      <span>Max image size 5 MB</span>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>

                <Input
                  className={styles.EventsNewEventTitleInput}
                  placeholder={'Add event title'}
                  {...register(firstInputLabel, {
                    required: 'Title is required',
                  })}
                  isValid={!errors?.[firstInputLabel]}
                  helperText={errors?.[firstInputLabel]?.message}
                  maxLength={120}
                />
                <Input
                  className={styles.EventsNewEventUrl}
                  type={'url'}
                  placeholder={'Add event URL'}
                  {...register(secondInputLabel, {
                    required: 'URL is required',
                    ...{
                      pattern: {
                        value: REGEX.URL,
                        message: 'Enter a valid url',
                      },
                    },
                  })}
                  isValid={!errors?.[secondInputLabel]}
                  helperText={errors?.[secondInputLabel]?.message}
                />
              </div>
            </div>
            <div className={styles.TeamAndEventModalFooter}>
              <div
                className={cn(styles.TeamAndEventModalFooterContent, {
                  [styles.isEditModal]: isEdit,
                })}
              >
                {isEdit ? (
                  <Button
                    className={styles.TeamAndEventModalFooterDelete}
                    onClick={() => {
                      typeof index === 'number' && onRemove(index);
                      onCancel();
                    }}
                    variant="text"
                    color="danger"
                    type="button"
                  >
                    Delete
                  </Button>
                ) : null}
                <Button
                  className={styles.TeamAndEventModalFooterConfirm}
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                  form="modal-form"
                >
                  Done
                </Button>
              </div>
            </div>
          </Form>
        </Modal>
      </>
    );
  }
);

TeamAndEventModal.displayName = 'TeamAndEventModal';
